import React, { useMemo } from "react";
import styled from "styled-components";
import moment from "moment";
import { BarChart } from "./BarChart";
import { REPETITION_PERIODS } from "../../constants";
import { color, radius, shadow, fontFamily } from "../Theme";
import { formatteMinutesToReadableFormat } from "../../utiles/timeformate";

const Card = styled.section`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  padding: 20px 24px;
  background: ${() => color("surface")};
  border-radius: ${() => radius("card")}px;
  box-shadow: ${() => shadow("card")};
  font-family: ${() => fontFamily()};
  @media (max-width: 599px) {
    padding: 16px;
  }
`;

const Head = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  margin-bottom: 16px;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  text-align: left;
  color: ${() => color("heading")};
`;

const Period = styled.span`
  font-size: 13px;
  line-height: 18px;
  font-variant-numeric: tabular-nums;
  color: ${() => color("text-muted")};
`;

const Summary = styled.span`
  margin-top: 8px;
  font-size: 32px;
  line-height: 40px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${() => color("heading")};
`;

const Plot = styled.div`
  position: relative;
  height: ${(p) => p.$height}px;
`;

export function periodCaption(view, date) {
  const format =
    view == REPETITION_PERIODS.MONTH
      ? "MMMM YYYY"
      : view == REPETITION_PERIODS.YEAR
        ? "YYYY"
        : "ddd DD MMMM YYYY";
  return moment(date).format(format);
}

/**
 * Analytics card: left-aligned title, the selected period, a summary number
 * (sum of the plotted values) and the bar chart.
 */
export default function ChartCard({ title, units, data, view, date, height = 260 }) {
  const total = useMemo(
    () => (data ?? []).reduce((acc, d) => acc + (Number(d.value) || 0), 0),
    [data]
  );
  const summary = units === "Mins" ? formatteMinutesToReadableFormat(total) : total;
  return (
    <Card>
      <Head>
        <Title>{title}</Title>
        {view && date && <Period>{periodCaption(view, date)}</Period>}
        <Summary>{summary}</Summary>
      </Head>
      <Plot $height={height}>
        <BarChart units={units} data={data} />
      </Plot>
    </Card>
  );
}
